import React, { useEffect, useMemo, useState, useContext } from "react";
import styles from "../../../styles/components/Modal/Filter/Section.module.css";
import { InputSelector } from "../../UI/InputSelector";
import { DropdownSelector } from "../../UI/DropdownSelector";
import { DateSelector } from "./DateSelector";
import { MultiSelectDropdown } from "../../UI/MultiSelectDropdown";

import { fieldsFilterConfigAdmin, fieldsConfigAffiliator } from "../../../utils/forms/leadFilterField";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { fieldLabels } from "../../../utils/forms/fieldsLabels";
import { loadLeadFilterOptions } from "../../../utils/leadFilterOptions";

const MULTI_SELECT_KEYS = [
    'manager_ids',
    'desk_ids',
    'reten_statuses',
    'sale_statuses',
    'geos',
    'affiliator_ids',
    'campaign_ids'
];

export const Section = ({ filters, setFilters, role, canListAffiliatorAccounts }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = fieldLabels[langSelected] || fieldLabels.ru;

    const [dropdownOptions, setDropdownOptions] = useState({});

    const fieldsConfig = useMemo(() => (role === "admin" ? fieldsFilterConfigAdmin : fieldsConfigAffiliator), [role]);

    const dropdownFieldsConfig = useMemo(() =>
        fieldsConfig.filter((field) => {
            if (role !== 'admin' || field.key !== 'affiliator_id') {
                return field.type === "dropdown";
            }
            return field.type === "dropdown" && canListAffiliatorAccounts;
        }),
        [fieldsConfig, role, canListAffiliatorAccounts]);

    const [sortOrder, setSortOrder] = useState(filters.order_by || '');

    const handleFilterChange = (filterName, value, formKey) => {
        setFilters((prev) => {
            const newFilters = { ...prev };

            if (MULTI_SELECT_KEYS.includes(formKey)) {
                if (Array.isArray(value) && value.length > 0) {
                    newFilters[formKey] = value;
                } else {
                    delete newFilters[formKey];
                }
            } else {
                const cleanedValue = value === undefined || value === null || (Array.isArray(value) && value.length === 0) ? '' : value;

                if (cleanedValue === '') {
                    delete newFilters[formKey];
                } else {
                    newFilters[formKey] = cleanedValue;
                }
            }
            return newFilters;
        });
    };

    const handleSortOrderChange = (filterName = null, value = null, filterKey = null) => {
        setSortOrder(value);
        setFilters((prev) => ({
            ...prev,
            order_by: value,
        }));
    };

    useEffect(() => {
        const loadDropdownOptions = async () => {
            const options = await loadLeadFilterOptions(dropdownFieldsConfig, {
                langSelected,
                sortLabels: t.sort,
            });
            setDropdownOptions(options);
        };

        if (dropdownFieldsConfig.length > 0) {
            loadDropdownOptions();
        }
    }, [dropdownFieldsConfig, t, langSelected]);

    const itemLabelValueClass = `${styles.itemLabelValue} ${theme === 'dark' ? styles.darkText : ''}`;

    return (
        <div className={styles.section}>
            <div className={styles.inputRow}>
                <div className={styles.itemLabel}>
                    <span className={itemLabelValueClass}>{t.sort.label}:</span>
                </div>
                <div className={styles.dropdownWrapper}>
                    <DropdownSelector
                        filterName="sortOrder"
                        filterKey="order_by"
                        selectedValue={sortOrder}
                        handleFilterChange={handleSortOrderChange}
                        options={dropdownOptions["sortOrder"] || []}
                        theme={theme}
                    />
                </div>
            </div>
            {fieldsConfig
                .filter(field => field.filter === true)
                .map(({ key, labelKey, formKey, type }) => {

                    if (role === 'admin' && key === 'affiliator_id' && !canListAffiliatorAccounts) {
                        return null;
                    }

                    const isMultiSelect = MULTI_SELECT_KEYS.includes(formKey);

                    return (
                        <div className={styles.inputRow} key={key}>
                            <div className={styles.itemLabel}>
                                <span className={itemLabelValueClass}>{t[labelKey] || labelKey}:</span>
                            </div>

                            {type === "input" && (
                                <InputSelector
                                    filterName={key}
                                    selectedValue={filters[formKey] || ''}
                                    handleFilterChange={(name, value) => handleFilterChange(name, value, formKey)}
                                    hasError={false}
                                    isFocused={false}
                                    onFocus={() => { }}
                                    onBlur={() => { }}
                                    theme={theme}
                                />
                            )}

                            {type === "dropdown" && isMultiSelect && (
                                <MultiSelectDropdown
                                    filterName={key}
                                    filterKey={formKey}
                                    selectedValue={Array.isArray(filters[formKey]) ? filters[formKey] : []}
                                    handleFilterChange={handleFilterChange}
                                    options={dropdownOptions[key] || []}
                                    shouldAutoClearOnMissingOptions={false}
                                    isSearchable
                                    theme={theme}
                                />
                            )}

                            {type === "dropdown" && !isMultiSelect && (
                                <DropdownSelector
                                    filterName={key}
                                    filterKey={formKey}
                                    selectedValue={Array.isArray(filters[formKey]) ? filters[formKey][0] : filters[formKey] || ''}
                                    handleFilterChange={handleFilterChange}
                                    options={dropdownOptions[key] || []}
                                    theme={theme}
                                />
                            )}

                            {type === "date" && (
                                <DateSelector
                                    filterName={key}
                                    selectedValue={filters[formKey] || ''}
                                    handleFilterChange={(name, value) => handleFilterChange(name, value, formKey)}
                                    theme={theme}
                                />
                            )}
                        </div>
                    );
                })}
        </div>
    );
};
